export type PrayerMethod =
  | 'Diyanet'
  | 'Fazilet'
  | 'TurkiyeTakvimi'
  | 'MWL'
  | 'ISNA'
  | 'Egypt'
  | 'Makkah'
  | 'Karachi'
  | 'Tehran'
  | 'Jafari'
  | 'France'
  | 'Russia'
  | 'Malaysia'
  | 'Singapore'
  | 'Shia'
  | 'Custom';

export interface PrayerTimesResult {
  fajr: number;
  sunrise: number;
  dhuhr: number;
  asr: number;
  maghrib: number;
  isha: number;
}

export interface PrayerMethodDefinition {
  id: PrayerMethod;
  label: string;
  region: string;
  fajrAngle: number;
  ishaAngle?: number;
  ishaMinutes?: number;
  ramadanIshaMinutes?: number;
  maghribAngle?: number;
  source: 'reference' | 'legacy';
  note?: string;
}

export const PRAYER_METHOD_ORDER: PrayerMethod[] = [
  'Diyanet',
  'Fazilet',
  'TurkiyeTakvimi',
  'MWL',
  'ISNA',
  'Egypt',
  'Makkah',
  'Karachi',
  'Tehran',
  'Jafari',
  'France',
  'Russia',
  'Malaysia',
  'Singapore',
  'Shia',
  'Custom',
];

export const PRAYER_METHODS: Record<PrayerMethod, PrayerMethodDefinition> = {
  Diyanet: {
    id: 'Diyanet',
    label: 'Diyanet – 4Eternity Preset',
    region: 'Türkei / Diyanet-Nutzer',
    fajrAngle: 18,
    ishaAngle: 18,
    source: 'legacy',
    note: 'Aus dem bisherigen 4Eternity-Unity-Stand übernommen. Offizielle Diyanet-Tabellen können zusätzliche lokale Korrekturen enthalten.',
  },
  Fazilet: {
    id: 'Fazilet',
    label: 'Fazilet Neşriyat – 4Eternity Preset',
    region: 'Türkischsprachiger Raum',
    fajrAngle: 19,
    ishaAngle: 17,
    source: 'legacy',
    note: 'Legacy-Preset aus der Unity-App; wird künftig gegen publizierte Tabellen validiert.',
  },
  TurkiyeTakvimi: {
    id: 'TurkiyeTakvimi',
    label: 'Türkiye Takvimi – 4Eternity Preset',
    region: 'Türkei / Europa',
    fajrAngle: 19,
    ishaAngle: 17,
    source: 'legacy',
    note: 'Legacy-Preset aus der Unity-App; wird künftig gegen publizierte Tabellen validiert.',
  },
  MWL: {
    id: 'MWL',
    label: 'Muslim World League',
    region: 'Europa, Fernost, Teile Nordamerikas',
    fajrAngle: 18,
    ishaAngle: 17,
    source: 'reference',
  },
  ISNA: {
    id: 'ISNA',
    label: 'ISNA',
    region: 'Nordamerika',
    fajrAngle: 15,
    ishaAngle: 15,
    source: 'reference',
  },
  Egypt: {
    id: 'Egypt',
    label: 'Egyptian General Authority of Survey',
    region: 'Ägypten / Teile Afrikas und Nahost',
    fajrAngle: 19.5,
    ishaAngle: 17.5,
    source: 'reference',
  },
  Makkah: {
    id: 'Makkah',
    label: 'Umm al-Qura, Makkah',
    region: 'Arabische Halbinsel',
    fajrAngle: 18.5,
    ishaMinutes: 90,
    ramadanIshaMinutes: 120,
    source: 'reference',
  },
  Karachi: {
    id: 'Karachi',
    label: 'University of Islamic Sciences, Karachi',
    region: 'Pakistan, Afghanistan, Bangladesch, Indien',
    fajrAngle: 18,
    ishaAngle: 18,
    source: 'reference',
  },
  Tehran: {
    id: 'Tehran',
    label: 'Institute of Geophysics, University of Tehran',
    region: 'Iran / Teile schiitischer Gemeinden',
    fajrAngle: 17.7,
    ishaAngle: 14,
    maghribAngle: 4.5,
    source: 'reference',
  },
  Jafari: {
    id: 'Jafari',
    label: 'Jafari / Leva Research Institute, Qom',
    region: 'Schiitische Gemeinden',
    fajrAngle: 16,
    ishaAngle: 14,
    maghribAngle: 4,
    source: 'reference',
  },
  France: {
    id: 'France',
    label: 'Muslims of France',
    region: 'Frankreich',
    fajrAngle: 12,
    ishaAngle: 12,
    source: 'reference',
  },
  Russia: {
    id: 'Russia',
    label: 'Spiritual Administration of Muslims of Russia',
    region: 'Russland',
    fajrAngle: 16,
    ishaAngle: 15,
    source: 'reference',
  },
  Malaysia: {
    id: 'Malaysia',
    label: 'Malaysia',
    region: 'Malaysia',
    fajrAngle: 20,
    ishaAngle: 18,
    source: 'reference',
  },
  Singapore: {
    id: 'Singapore',
    label: 'Islamic Religious Council of Singapore',
    region: 'Singapur',
    fajrAngle: 20,
    ishaAngle: 18,
    source: 'reference',
  },
  Shia: {
    id: 'Shia',
    label: 'Shia – 4Eternity Legacy',
    region: 'Legacy-Kompatibilität',
    fajrAngle: 16,
    ishaAngle: 14,
    source: 'legacy',
    note: 'Behält das frühere 4Eternity-Verhalten mit zusätzlichen Dhuhr-/Maghrib-Korrekturen bei. Für neue Nutzer ist Jafari die sauberere Referenzmethode.',
  },
  Custom: {
    id: 'Custom',
    label: 'Custom – Unity Standardwerte',
    region: 'Benutzerdefiniert / Legacy',
    fajrAngle: 19,
    ishaAngle: 17,
    source: 'legacy',
  },
};

const toRad = (value: number) => (value * Math.PI) / 180;
const toDeg = (value: number) => (value * 180) / Math.PI;

const fixHour = (value: number) => {
  const normalized = value - 24 * Math.floor(value / 24);
  return normalized < 0 ? normalized + 24 : normalized;
};

const fixAngle = (value: number) => {
  const normalized = value - 360 * Math.floor(value / 360);
  return normalized < 0 ? normalized + 360 : normalized;
};

const timeDiff = (from: number, to: number) => fixHour(to - from);

function julianDate(date: Date): number {
  let year = date.getFullYear();
  let month = date.getMonth() + 1;
  const day = date.getDate();

  if (month <= 2) {
    year -= 1;
    month += 12;
  }

  const a = Math.floor(year / 100);
  const b = 2 - a + Math.floor(a / 4);
  return Math.floor(365.25 * (year + 4716)) + Math.floor(30.6001 * (month + 1)) + day + b - 1524.5;
}

function solarValues(jd: number): { declination: number; equationOfTime: number } {
  const days = jd - 2451545.0;
  const centuries = days / 36525.0;
  const meanAnomaly = fixAngle(357.52911 + centuries * (35999.05029 - 0.0001537 * centuries));
  const meanLongitude = fixAngle(280.46646 + centuries * (36000.76983 + centuries * 0.0003032));
  const center =
    (1.914602 - centuries * (0.004817 + 0.000014 * centuries)) * Math.sin(toRad(meanAnomaly)) +
    (0.019993 - 0.000101 * centuries) * Math.sin(toRad(2 * meanAnomaly)) +
    0.000289 * Math.sin(toRad(3 * meanAnomaly));
  const trueLongitude = meanLongitude + center;
  const obliquity = 23.439291 - centuries * 0.0130042;
  const declination = toDeg(Math.asin(Math.sin(toRad(obliquity)) * Math.sin(toRad(trueLongitude))));
  const rightAscension = toDeg(
    Math.atan2(
      Math.cos(toRad(obliquity)) * Math.sin(toRad(trueLongitude)),
      Math.cos(toRad(trueLongitude)),
    ),
  );
  const equationOfTime = meanLongitude / 15 - fixHour(rightAscension / 15);
  return { declination, equationOfTime };
}

function timeZoneOffsetHours(date: Date, timeZone?: string): number {
  if (!timeZone) return -date.getTimezoneOffset() / 60;

  try {
    const formatter = new Intl.DateTimeFormat('en-CA', {
      timeZone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hourCycle: 'h23',
    });
    const parts = formatter.formatToParts(date);
    const values = Object.fromEntries(parts.map((part) => [part.type, part.value]));
    const representedAsUtc = Date.UTC(
      Number(values.year),
      Number(values.month) - 1,
      Number(values.day),
      Number(values.hour),
      Number(values.minute),
      Number(values.second),
    );
    return (representedAsUtc - date.getTime()) / 3_600_000;
  } catch {
    return -date.getTimezoneOffset() / 60;
  }
}

export function dateForTimeZone(now: Date, timeZone?: string): Date {
  if (!timeZone) return new Date(now);
  try {
    const formatter = new Intl.DateTimeFormat('en-CA', {
      timeZone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    });
    const parts = formatter.formatToParts(now);
    const values = Object.fromEntries(parts.map((part) => [part.type, part.value]));
    return new Date(Number(values.year), Number(values.month) - 1, Number(values.day), 12, 0, 0, 0);
  } catch {
    return new Date(now);
  }
}

export function decimalHourInTimeZone(now: Date, timeZone?: string): number {
  if (!timeZone) return now.getHours() + now.getMinutes() / 60 + now.getSeconds() / 3600;
  try {
    const formatter = new Intl.DateTimeFormat('en-GB', {
      timeZone,
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hourCycle: 'h23',
    });
    const parts = formatter.formatToParts(now);
    const values = Object.fromEntries(parts.map((part) => [part.type, part.value]));
    return Number(values.hour) + Number(values.minute) / 60 + Number(values.second) / 3600;
  } catch {
    return now.getHours() + now.getMinutes() / 60 + now.getSeconds() / 3600;
  }
}

function isRamadan(date: Date, timeZone?: string): boolean {
  try {
    const formatter = new Intl.DateTimeFormat('en-u-ca-islamic', { month: 'numeric', timeZone });
    const month = Number(formatter.format(date));
    if (Number.isFinite(month)) return month === 9;
  } catch {
    // Fallback below.
  }

  const knownRamadanStart = new Date(2023, 2, 23);
  const days = Math.floor((date.getTime() - knownRamadanStart.getTime()) / 86_400_000);
  const phase = days / 29.53 - Math.floor(days / 29.53);
  return phase < 1 / 12;
}

function safeTime(value: number): number {
  return Number.isFinite(value) ? fixHour(value) : Number.NaN;
}

function cappedTwilightBeforeSunrise(time: number, sunrise: number, nightLength: number, angle: number): number {
  const allowed = nightLength * (angle / 60);
  if (!Number.isFinite(time) || timeDiff(time, sunrise) > allowed) return fixHour(sunrise - allowed);
  return time;
}

function cappedTwilightAfterSunset(time: number, sunset: number, nightLength: number, angle: number): number {
  const allowed = nightLength * (angle / 60);
  if (!Number.isFinite(time) || timeDiff(sunset, time) > allowed) return fixHour(sunset + allowed);
  return time;
}

export function calculatePrayerTimes(
  date: Date,
  latitude: number,
  longitude: number,
  method: PrayerMethod,
  hanafi: boolean,
  timeZone?: string,
): PrayerTimesResult {
  const definition = PRAYER_METHODS[method] ?? PRAYER_METHODS.Diyanet;
  const jd = julianDate(date);
  const solar = solarValues(jd);
  const probe = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate(), 12, 0, 0));
  const timezoneOffset = timeZoneOffsetHours(probe, timeZone);

  const midDay = (values: { equationOfTime: number }) => fixHour(12 - values.equationOfTime);
  const adjustTime = (time: number, offset = timezoneOffset) => safeTime(time + offset - longitude / 15);

  const computeTime = (
    zenithAngle: number,
    afternoon: boolean,
    values = solar,
    offset = timezoneOffset,
  ) => {
    const phi = toRad(latitude);
    const delta = toRad(values.declination);
    const z = toRad(zenithAngle);
    const cosH = (Math.cos(z) - Math.sin(phi) * Math.sin(delta)) / (Math.cos(phi) * Math.cos(delta));
    if (cosH > 1 || cosH < -1) return Number.NaN;
    const hourAngle = toDeg(Math.acos(Math.max(-1, Math.min(1, cosH))));
    return adjustTime(midDay(values) + (afternoon ? hourAngle / 15 : -hourAngle / 15), offset);
  };

  let fajr = computeTime(90 + definition.fajrAngle, false);
  const sunrise = computeTime(90.833, false);
  let dhuhr = adjustTime(midDay(solar));

  const shadowFactor = hanafi ? 2 : 1;
  const phi = toRad(latitude);
  const delta = toRad(solar.declination);
  const asrAngle = toDeg(Math.atan(1 / (shadowFactor + Math.tan(Math.abs(phi - delta)))));
  const asr = computeTime(90 - asrAngle, true);

  const sunset = computeTime(90.833, true);
  let maghrib = definition.maghribAngle
    ? computeTime(90 + definition.maghribAngle, true)
    : sunset;
  let isha = definition.ishaAngle
    ? computeTime(90 + definition.ishaAngle, true)
    : Number.NaN;

  if (definition.ishaMinutes) {
    const minutes = definition.ramadanIshaMinutes && isRamadan(date, timeZone)
      ? definition.ramadanIshaMinutes
      : definition.ishaMinutes;
    isha = fixHour(maghrib + minutes / 60);
  }

  // Angle-based higher-latitude fallback. It also caps unusually long twilight
  // calculations to the accepted fraction of the night.
  if (Number.isFinite(sunrise) && Number.isFinite(sunset)) {
    const nextDate = new Date(date);
    nextDate.setDate(nextDate.getDate() + 1);
    const nextJd = julianDate(nextDate);
    const nextSolar = solarValues(nextJd);
    const nextProbe = new Date(Date.UTC(nextDate.getFullYear(), nextDate.getMonth(), nextDate.getDate(), 12, 0, 0));
    const nextOffset = timeZoneOffsetHours(nextProbe, timeZone);
    let nextSunrise = computeTime(90.833, false, nextSolar, nextOffset);
    if (Number.isFinite(nextSunrise)) {
      nextSunrise += 24;
      const nightLength = nextSunrise - sunset;
      if (nightLength > 0 && nightLength < 24) {
        fajr = cappedTwilightBeforeSunrise(fajr, sunrise, nightLength, definition.fajrAngle);
        if (definition.ishaAngle) {
          isha = cappedTwilightAfterSunset(isha, sunset, nightLength, definition.ishaAngle);
        }
        if (definition.maghribAngle) {
          maghrib = cappedTwilightAfterSunset(maghrib, sunset, nightLength, definition.maghribAngle);
        }
      }
    }
  }

  if (method === 'Shia') {
    maghrib = fixHour(sunset + 17 / 60);
    dhuhr = fixHour(dhuhr + 5 / 60);
  }

  return { fajr, sunrise, dhuhr, asr, maghrib, isha };
}

export function decimalHourToDate(date: Date, decimalHour: number): Date | null {
  if (!Number.isFinite(decimalHour)) return null;
  const result = new Date(date);
  result.setHours(0, 0, 0, 0);
  const totalMinutes = Math.round(decimalHour * 60);
  result.setMinutes(totalMinutes);
  return result;
}

export function formatDecimalHour(decimalHour: number): string {
  if (!Number.isFinite(decimalHour)) return '–';
  const totalMinutes = Math.round(fixHour(decimalHour) * 60) % (24 * 60);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
}
